import './SearchBox.scss';

import { Input } from 'antd';
import { useCustomTranslation } from 'helpers/translationHelper';
import { removeDiacritics } from 'helpers/validation';
import React, { useImperativeHandle, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';

import type { SearchBoxState } from './useSearchBoxState';

const { Search } = Input;

interface Props {
  onValueChanged: (value: string) => void;
  placeholder?: string;
  initialValue?: string;
  state?: SearchBoxState;
}

export interface SearchBoxResetHandle {
  resetSearchBox: () => void;
}

const SearchBox = React.forwardRef<SearchBoxResetHandle, Props>((props, ref) => {
  const { onValueChanged, placeholder, initialValue, state } = props;

  const { t } = useCustomTranslation();

  const [internalValue, setInternalValue] = useState(initialValue || '');

  const publishOnValueChanged = useDebouncedCallback(() => {
    if (onValueChanged) {
      onValueChanged(removeDiacritics(internalValue).toLocaleLowerCase());
    }
  }, 700);

  const onValueChangedInternal = (event: any) => {
    if (state) {
      state.onChange(event.target.value);
      return;
    }
    setInternalValue(event.target.value);
    publishOnValueChanged();
  };

  const buttonClick = () => {
    if (state) {
      state.onSearch();
      return;
    }
    publishOnValueChanged.cancel();

    if (onValueChanged) {
      onValueChanged(removeDiacritics(internalValue).toLocaleLowerCase());
    }
  };

  useImperativeHandle(
    ref,
    () => ({
      resetSearchBox: () => {
        if (state) {
          state.reset();
          return;
        }
        setInternalValue('');
        onValueChanged('');
      }
    }),
    [onValueChanged, state]
  );

  return (
    <Search
      className="SearchBox"
      placeholder={placeholder || t('common.search.placeholder')}
      onChange={onValueChangedInternal}
      onSearch={buttonClick}
      value={state?.value ?? internalValue}
    />
  );
});

export default SearchBox;
